import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { Label } from "@/shared/ui/label";
import type { CoachingTaskItem, TaskUpdateRequest } from "../../api/coachingService";

type Props = {
  task: CoachingTaskItem | null;
  onClose: () => void;
  onSubmit: (id: number, data: TaskUpdateRequest) => void;
  isPending: boolean;
};

export function TaskEditDialog({ task, onClose, onSubmit, isPending }: Props) {
  const { t } = useTranslation("coaching");
  return (
    <Dialog open={task !== null} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("taskEdit.title")}</DialogTitle>
        </DialogHeader>
        {task && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const fd = new FormData(e.currentTarget);
              // ★空字符串 = 没填 = 不改这一项;但 "0" 是**合法的及格线**(这次全员通过),
              //   所以判空只能看空串,不能写 `Number(x) || undefined` —— 那会把 0 一起吃掉。
              const num = (k: string) => {
                const raw = (fd.get(k) as string)?.trim();
                return raw ? Number(raw) : undefined;
              };
              onSubmit(task.id, {
                name: fd.get("name") as string,
                deadline: (fd.get("deadline") as string) || undefined,
                status: fd.get("status") as "active" | "closed",
                passScore: num("passScore"),
                maxRounds: num("maxRounds"),
              });
            }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label>{t("taskEdit.name")}</Label>
              <Input name="name" required defaultValue={task.name} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("taskEdit.deadline")}</Label>
                <Input
                  name="deadline"
                  type="datetime-local"
                  defaultValue={task.deadline?.slice(0, 16) ?? ""}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("taskEdit.status")}</Label>
                {/* 只列后端真有的两个状态。此前还列着 DISPATCHING / ARCHIVED —— 后端一个都不认,
                    选了就是写进一个没人认得的状态,列表筛选与补考闸都会跟着错。 */}
                <select
                  name="status"
                  defaultValue={task.status?.toLowerCase() === "closed" ? "closed" : "active"}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="active">{t("taskEdit.statusActive")}</option>
                  <option value="closed">{t("taskEdit.statusClosed")}</option>
                </select>
              </div>
            </div>
            {/* V34/V35:建任务能填,改任务此前填不了 —— 建完就再也调不动这条线。 */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("taskEdit.passScore")}</Label>
                <Input
                  name="passScore"
                  type="number"
                  min={0}
                  max={100}
                  placeholder={t("taskEdit.passScorePlaceholder")}
                  defaultValue={task.passScore ?? ""}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("taskEdit.maxRounds")}</Label>
                <Input
                  name="maxRounds"
                  type="number"
                  min={1}
                  max={50}
                  placeholder={t("taskEdit.maxRoundsPlaceholder")}
                  defaultValue={task.maxRounds ?? ""}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t("taskEdit.note")}</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t("taskEdit.cancel")}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {t("taskEdit.save")}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
